// Pubsub message types //
import { AvRefreshReasons } from "@minervaproject/avsubsystem-core";

import { ClassEventType } from "./class_event";
import { ClassroomSliceStateType } from "./classroom";
import { MonitoringSliceStateType } from "./monitoring";
import { MonitoringEventType } from "./monitoring_event";
import { BandwidthModeType } from "./user";

export enum PageUnloadReasonType {
  CLASS_EXPIRED = "class-expired",
  CLASS_NOT_IN_FLIGHT = "class-not-in-flight",
  CLASS_NOT_IN_FLIGHT_FOR_MONITORING = "class-not-in-flight-for-monitoring",
  CLICKED_EXIT = "clicked-exit",
  CLICKED_LOGO = "clicked-logo",
  FORCE_RELOAD_PAGE_EVERYBODY = "force-reload-page-everybody",
  FORCE_RELOAD_PAGE_HARD_RESET_CLASS = "force-reload-page-hard-reset-class",
  FORCE_RELOAD_PAGE_INDIVIDUAL = "force-reload-page-individual",
  FORCE_RELOAD_PAGE_SOFT_RESET_CLASS = "force-reload-page-soft-reset-class",
  KICKED_OUT_OF_CLASS = "kicked-out-of-class",
  KICKED_OUT_OF_CLASS_INACTIVE_USER = "kicked-out-of-class-inactive-user",
  KICKED_OUT_OF_CLASS_NEW_TAB = "kicked-out-of-class-new-tab",
  KICKED_OUT_OF_CLASS_SERVER_REASSIGNMENT = "kicked-out-of-class-server-reassignment",
  LOGGED_INTO_WRONG_ORG = "logged-into-wrong-org",
  NOT_LOGGED_IN = "not-logged-in",
  NOT_PERMITTED_TO_ENTER_CLASSROOM = "not-permitted-to-enter-classroom",
  NOT_PERMITTED_TO_MONITOR_CLASS = "not-permitted-to-monitor-class",
  PAGE_NOT_FOUND = "page-not-found",
  RELOAD_DUE_TO_MIGRATION = "reload-due-to-migration",
  TOO_EARLY_TO_ENTER_CLASSROOM = "too-early-to-enter-classroom",
  UNKNOWN = "unknown",
}

export interface StateMessageType {
  type: "action";
  action: PublishableActionType;
}

export interface RequestSyncMessageType {
  [key: string]: unknown;
  type: "request-sync";
  id?: string;
}

export interface PingMessageType {
  type: "ping";
  startTimestamp: number;
}

export interface UnknownMessageType {
  type: string;
}

export type MessageFromClientToServerType =
  PingMessageType | StateMessageType | RequestSyncMessageType;

export type ClassroomActionTypeType =
  | "classroom/upsertState"
  | "classroom/join"
  | "classroom/leave"
  | "classroom/sendPubsubMessage"
  | "classroom/softReset"
  | "classroom/hardReset"
  | "classroom/replaceUserInParticipantBar"
  | "classroom/setCurrentFilter"
  | "classroom/featureBreakoutRoomResource"
  | "classroom/featureContentInOneUp"
  | "classroom/setFeaturedInModal"
  | "classroom/setStagePane"
  | "classroom/setStagePaneIsPinned"
  | "classroom/setAllStagePanes"
  | "classroom/clearAllStagePanes"
  | "classroom/setActiveTimelineSegmentId"
  | "classroom/setAvService"
  | "classroom/setClassType"
  | "classroom/setEnableStudentDrawing"
  | "classroom/setIsInFlight"
  | "classroom/setBandwidthModeForUser"
  | "classroom/setBlurBackground"
  | "classroom/setPttModeForUser"
  | "classroom/setPttModeForClass"
  | "classroom/setMaxSubscribedStreams"
  | "classroom/setUserCameraError"
  | "classroom/setUserConnectionQuality"
  | "classroom/setUserConnectionState"
  | "classroom/setUserIsPerformingAvCheck"
  | "classroom/setUserMicError"
  | "classroom/setUserPubsubConnectionQuality"
  | "classroom/setUserStreamState"
  | "classroom/setUserPreferredDevices"
  | "classroom/muteAllExceptInstructor"
  | "classroom/addChat"
  | "classroom/likeOrUnlikeChat"
  | "classroom/setUserVote"
  | "classroom/setUserZoomPreference"
  | "classroom/toggleUserHandRaised"
  | "classroom/clearAllSignals"
  | "classroom/setRecordingSessionId"
  | "classroom/setRecordingState"
  | "classroom/addBreakout"
  | "classroom/clearAllBreakoutGroupTitles"
  | "classroom/setAllBreakoutGroupTitles"
  | "classroom/setBreakoutRooms"
  | "classroom/setResources"
  | "classroom/startBreakoutSession"
  | "classroom/adjustBreakoutSessionDuration"
  | "classroom/moveBreakoutRoomUser"
  | "classroom/assignUserToLeastPopulatedBreakoutRoom"
  | "classroom/setBreakoutRoomIsPinned"
  | "classroom/visitBreakoutRoom"
  | "classroom/stopVisitingBreakoutRoom"
  | "classroom/listenToBreakoutRoom"
  | "classroom/stopListeningToBreakoutRoom"
  | "classroom/endBreakoutSession"
  | "classroom/playPauseTimer"
  | "classroom/adjustTimerDuration"
  | "classroom/endTimer"
  | "classroom/addBreakoutTalkTime"
  | "classroom/addClassroomTalkTime"
  | "classroom/setUserHasClassroomInFocus"
  | "classroom/setUserPresenceData"
  | "classroom/createNewPoll"
  | "classroom/startPollSession"
  | "classroom/endPollSession"
  | "classroom/setPollSessionViewResultsAs"
  | "classroom/createOrUpdatePollResponse"
  | "classroom/makePollResponsesVisibleToStudents"
  | "classroom/featurePollResponse"
  | "classroom/hideFeaturedPollResponse"
  | "classroom/addResource"
  | "classroom/deleteScreenshareResource"
  | "classroom/setResourceOwnerId"
  | "classroom/setResourcePage"
  | "classroom/setResourcePresentationViewScrollPosition"
  | "classroom/setResourceTitle"
  | "classroom/setResourceVideoState"
  | "classroom/setResourceVideoTimeInSeconds"
  | "classroom/setResourceResetWhiteboard"
  | "classroom/setUserEnableLiveCaptions"
  | "classroom/setLatestCaption"
  | "classroom/setLastSnapshotTime"
  | "classroom/_replaceUserOnStage"
  | "classroom/_setParticipantBarUserIds"
  | "classroom/_setUserReaction"
  | "classroom/_setUserIsSpeaking";

export interface ClassroomActionType {
  [key: string]: unknown;
  type: ClassroomActionTypeType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  forcePublish?: boolean;
}

export type MonitoringActionTypeType =
  | "monitoring/_createMonitoringEvent"
  | "monitoring/createMonitoringEvent"
  | "monitoring/_createClassEvent"
  | "monitoring/createClassEvent"
  | "monitoring/deleteAllClassEvents";

export interface MonitoringActionType {
  [key: string]: unknown;
  type: MonitoringActionTypeType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  forcePublish: undefined;
}

export type LocalActionTypeType =
  | "local/clearPubsubMessage"
  | "local/setChatTextAreaContent"
  | "local/setChatTextAreaContentOverride"
  | "local/setCurrentUserId"
  | "local/setCurrentlyBackingUpPoll"
  | "local/setHasNewChatMessages"
  | "local/setHasStageChanged"
  | "local/setKeyboardShortcutSignal"
  | "local/setOverlayError"
  | "local/setPubsubErrorCode"
  | "local/setPubsubMessage"
  | "local/showLocalModal"
  | "local/hideLocalModal"
  | "local/setShowInstructorNotes"
  | "local/setSidebarPanel"
  | "local/toggleSidebarPanel"
  | "local/addPreclassCheckResult"
  | "local/setJoinTimestamp";

export interface LocalActionType {
  [key: string]: unknown;
  type: LocalActionTypeType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  forcePublish: undefined;
}

export type LocalMonitoringActionTypeType =
  | "setIntercomConversations"
  | "setMonitoringEventTableEventsPerPage"
  | "setMonitoringEventTableLevels"
  | "setMonitoringEventTableTypes"
  | "setMonitoringEventTableUsers"
  | "setStartTimeFilter"
  | "setEndTimeFilter"
  | "setTimeFilter";

export interface LocalMonitoringActionType {
  [key: string]: unknown;
  type: LocalMonitoringActionTypeType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  forcePublish: undefined;
}

export type WorkbookActionTypeType = "workbook/setQuestion" | "workbook/setWorkbook";

export interface WorkbookActionType {
  [key: string]: unknown;
  type: WorkbookActionTypeType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  forcePublish: undefined;
}

export type AvActionTypeType =
  | "av/addStream"
  | "av/removeStream"
  | "av/clearAllStreamsState"
  | "av/clearAllState"
  | "av/updateStreamState"
  | "av/updateStreamRetries"
  | "av/updateStreamEnableAudio"
  | "av/updateStreamAudioAvailable"
  | "av/updateStreamEnableVideo"
  | "av/updateStreamVideoAvailable"
  | "av/updateStreamRequestCopy"
  | "av/updateStreamQualityLevel"
  | "av/updateAggregatedStats"
  | "av/updateStreamStats"
  | "av/updateConnectionState"
  | "av/updateConnectionAvService"
  | "av/updateConnectionBandwidthMode"
  | "av/updateConnectionQualityLevel"
  | "av/setRoomId"
  | "av/updateDeviceList"
  | "av/updateDefaultDevices"
  | "av/updateSelectedDevices"
  | "av/updateSelectedAudioOutput"
  | "av/updateDeviceGlobalState"
  | "av/updateDeviceGlobalError"
  | "av/clearDeviceGlobalErrors"
  | "av/updateDeviceGlobalDisabled"
  | "av/updateBlurState";

export interface AvActionType {
  [key: string]: unknown;
  type: AvActionTypeType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  forcePublish: undefined;
}

export type ReduxActionBaseType =
  | ClassroomActionType
  | MonitoringActionType
  | LocalActionType
  | LocalMonitoringActionType
  | WorkbookActionType
  | AvActionType;

export type ActionDirectlyFromClientType = ReduxActionBaseType & {
  meta: undefined;
};

export type SocketTypeMetaType = {
  socketType: "classroom" | "monitoring";
};

export interface InitActionType {
  [key: string]: unknown;
  type: "INIT";
}

export type ReduxActionMetaType = {
  origin?: "client" | "server";
};

export type PublishableActionMetaType = {
  psid: string;
  clientTimestamp?: number;
  publisherId?: string;
};

export type ServerActionMetaType = {
  origin: "server";
};

export type PublishableActionType = ReduxActionBaseType & {
  meta: PublishableActionMetaType;
};

export const hasPublishableMeta = (action: ReduxActionBaseType): action is PublishableActionType =>
  "meta" in action && typeof action.meta === "object" && action.meta !== null;

export type AckedActionType = ReduxActionBaseType & {
  meta: PublishableActionMetaType & ServerActionMetaType;
};

export interface AckMessageType {
  [key: string]: unknown;
  type: "ack";
  accepted: boolean;
  psid: string;
  nextVersion: number;
}

export interface ActionMessageType {
  type: "action";
  action: AckedActionType;
  baseVersion: number;
  nextVersion: number;
}

export enum PubsubMessageTypeType {
  FORCE_BANDWIDTH_MODE_CHANGE = "force-bandwidth-mode-change",
  KICK_USER_OUT_WITH_WARNING = "kick-user-out-with-warning",
  KICK_USER_OUT_WITHOUT_WARNING = "kick-user-out-without-warning",
  REFRESH_AV = "refresh-av",
  RELOAD_PAGE = "reload-page",
  RUN_CONFETTI = "run-confetti",
  UPLOAD_LOGS_TO_S3 = "upload-logs-to-s3",
}

interface ForceBandwidthModeChangePubsubMessageType {
  type: PubsubMessageTypeType.FORCE_BANDWIDTH_MODE_CHANGE;
  data: {
    bandwidthMode: BandwidthModeType;
    targetUserId: string;
  };
}

interface KickUserOutWithWarningPubsubMessageType {
  type: PubsubMessageTypeType.KICK_USER_OUT_WITH_WARNING;
}

interface KickUserOutWithoutWarningPubsubMessageType {
  type: PubsubMessageTypeType.KICK_USER_OUT_WITHOUT_WARNING;
  data: {
    targetUserId: string;
    reason: PageUnloadReasonType;
  };
}

interface RefreshAvPubsubMessageType {
  type: PubsubMessageTypeType.REFRESH_AV;
  data: {
    reason: AvRefreshReasons;
    targetUserId: string;
  };
}

interface ReloadPagePubsubMessageType {
  type: PubsubMessageTypeType.RELOAD_PAGE;
  data: {
    reason: PageUnloadReasonType;
    targetUserId?: string;
  };
}

interface RunConfettiPubsubMessageType {
  type: PubsubMessageTypeType.RUN_CONFETTI;
}

interface UploadLogsPubsubMessageType {
  type: PubsubMessageTypeType.UPLOAD_LOGS_TO_S3;
  data: {
    reportTitle: string;
    message: string;
    timestamp: number;
  };
}

export type PubsubMessageType = (
  | ForceBandwidthModeChangePubsubMessageType
  | KickUserOutWithWarningPubsubMessageType
  | KickUserOutWithoutWarningPubsubMessageType
  | RefreshAvPubsubMessageType
  | ReloadPagePubsubMessageType
  | RunConfettiPubsubMessageType
  | UploadLogsPubsubMessageType
) & {
  userId: string;
};

export enum PubsubErrorCodes {
  CLASS_EXPIRED = "class_expired",
  CLASS_NOT_IN_FLIGHT = "class_not_in_flight",
  MIGRATION_FAILED = "migration_failed",
}

export interface ErrorMessageType {
  type: "error";
  code: PubsubErrorCodes;
  message: string;
  requestId?: string;
}

export interface PongMessageType {
  type: "pong";
  startTimestamp: number;
}

export interface SyncMessageType {
  [key: string]: unknown;
  type: "sync";
  state: ClassroomSliceStateType | MonitoringSliceStateType;
  version: number;
  requestId?: string;
}

type OmitKeepIndex<T, K extends keyof T> = { [P in keyof T as P extends K ? never : P]: T[P] };

export type RawActionFromServerType =
  | OmitKeepIndex<AckMessageType, "nextVersion">
  | OmitKeepIndex<SyncMessageType, "version" | "requestId">;

export type AnnotatedActionFromServerType =
  | ((RawActionFromServerType | RequestSyncMessageType) & {
      meta: SocketTypeMetaType;
    })
  | (ReduxActionBaseType & {
      meta: PublishableActionMetaType & ServerActionMetaType & SocketTypeMetaType;
    });

// The origianl reducers (e.g. classroomSliceReducer, ...) will either receive actions from the client (raw if it comes straight from client or
// annotated if it lives in pendingActions) or annotated actions from the server.
export type OriginalReducerActionType =
  | ActionDirectlyFromClientType
  | PublishableActionType
  | AnnotatedActionFromServerType
  | InitActionType;

// The enhanced reducers (e.g. BaseReduxPubsubSlice.getReducer(), ...) will receive actions directly from the client or annotated actions from the server.
export type EnhancedReducerActionType =
  ActionDirectlyFromClientType | AnnotatedActionFromServerType;

export type MessageFromServerToClientType =
  AckMessageType | ActionMessageType | ErrorMessageType | PongMessageType | SyncMessageType;

export type RTTStatsType = {
  min: number;
  max: number;
  median: number | undefined;
};

export type PubsubStatsType = {
  msgCount: number;
  mostCommonMsg: string | undefined;
  pingRTT: RTTStatsType;
  rtt: RTTStatsType & { slowestMsg: string | undefined };
};

export interface PubsubStatsCollectorType {
  addPingRecord: (rtt: number) => void;
  addRecord: (action: PublishableActionType) => void;
  getStats: () => PubsubStatsType;
  isPingHigh: () => boolean;
  isPingLow: () => boolean;
  resetStats: () => void;
}

export type PendingEventType = {
  classId: string;
  payload: Omit<ClassEventType, "id" | "timestamp"> | Omit<MonitoringEventType, "id" | "timestamp">;
  eventKind: "ClassEvent" | "MonitoringEvent";
};

export type QueueItemType = string | PendingEventType;
